import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Link } from 'react-router-dom'
import { formatCurrency } from '@/lib/constants'

/**
 * Market Data Page — Status dashboard showing cached data freshness
 * ALL data reads from Supabase (source of truth), not Zustand ephemeral state.
 */

const SIX_HOURS_MS = 6 * 60 * 60 * 1000

function formatAge(isoString: string | null): string {
  if (!isoString) return 'Never'
  const ms = Date.now() - new Date(isoString).getTime()
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

function getFreshnessColor(isoString: string | null): string {
  if (!isoString) return 'text-red-600'
  const ms = Date.now() - new Date(isoString).getTime()
  if (ms < 2 * 60 * 60 * 1000) return 'text-green-600'
  if (ms < SIX_HOURS_MS) return 'text-amber-600'
  return 'text-red-600'
}

interface MarketDataStats {
  priceCount: number
  latestPriceUpdate: string | null
  earningsCount: number
  smaCount: number
  latestSmaUpdate: string | null
  optionCacheCount: number
  optionCacheFreshCount: number
  optionCacheEntries: { ticker: string; fetched_at: string }[]
  indicatorCount: number
  latestIndicatorUpdate: string | null
  indicatorEntries: { ticker: string; updated_at: string }[]
}

export default function MarketDataPage() {
  const [stats, setStats] = useState<MarketDataStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchStats() {
      setLoading(true)
      const result: MarketDataStats = {
        priceCount: 0,
        latestPriceUpdate: null,
        earningsCount: 0,
        smaCount: 0,
        latestSmaUpdate: null,
        optionCacheCount: 0,
        optionCacheFreshCount: 0,
        optionCacheEntries: [],
        indicatorCount: 0,
        latestIndicatorUpdate: null,
        indicatorEntries: [],
      }

      // 1. Price + earnings stats from watchlist table
      try {
        const { data: watchlistRows } = await supabase
          .from('watchlist')
          .select('ticker, price_current, price_updated_at, sma_50, sma_updated_at, earnings_date')

        if (watchlistRows) {
          // Count tickers with prices
          result.priceCount = watchlistRows.filter(r => r.price_current && Number(r.price_current) > 0).length

          // Find latest price update
          let latestPrice: string | null = null
          for (const row of watchlistRows) {
            if (row.price_updated_at && (!latestPrice || row.price_updated_at > latestPrice)) {
              latestPrice = row.price_updated_at
            }
          }
          result.latestPriceUpdate = latestPrice

          // Count tickers with SMAs
          result.smaCount = watchlistRows.filter(r => r.sma_50 && Number(r.sma_50) > 0).length

          // Find latest SMA update
          let latestSma: string | null = null
          for (const row of watchlistRows) {
            if (row.sma_updated_at && (!latestSma || row.sma_updated_at > latestSma)) {
              latestSma = row.sma_updated_at
            }
          }
          result.latestSmaUpdate = latestSma

          // Count tickers with earnings dates
          result.earningsCount = watchlistRows.filter(r => r.earnings_date).length
        }
      } catch { /* table may not exist */ }

      // 3. Option chain cache from option_chain_cache table
      try {
        const { data: optionRows } = await supabase
          .from('option_chain_cache')
          .select('ticker, fetched_at')
          .order('fetched_at', { ascending: false })

        if (optionRows) {
          result.optionCacheCount = optionRows.length
          result.optionCacheFreshCount = optionRows.filter(r =>
            r.fetched_at && (Date.now() - new Date(r.fetched_at).getTime()) < SIX_HOURS_MS
          ).length
          result.optionCacheEntries = optionRows.slice(0, 10)
        }
      } catch { /* table may not exist */ }

      // 4. Technical indicators from technical_indicators table
      try {
        const { data: indicatorRows } = await supabase
          .from('technical_indicators')
          .select('ticker, updated_at')
          .order('updated_at', { ascending: false })

        if (indicatorRows) {
          result.indicatorCount = indicatorRows.length
          let latest: string | null = null
          for (const row of indicatorRows) {
            if (row.updated_at && (!latest || row.updated_at > latest)) {
              latest = row.updated_at
            }
          }
          result.latestIndicatorUpdate = latest
          result.indicatorEntries = indicatorRows.slice(0, 10)
        }
      } catch { /* table may not exist */ }

      setStats(result)
      setLoading(false)
    }

    fetchStats()
  }, [])

  if (loading || !stats) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-xl font-bold text-gray-900 mb-6">📊 Market Data Status</h1>
        <p className="text-sm text-gray-500">Loading data from Supabase...</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-xl font-bold text-gray-900 mb-6">📊 Market Data Status</h1>
      <p className="text-sm text-gray-600 mb-6">
        Overview of cached market data (source: Supabase). Prices and SMAs are refreshed from the Watchlist page.
        Option chains and indicators are fetched on-demand.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Price + Earnings Status */}
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">💰 Prices + Earnings</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Last price refresh:</span>
              <span className={`font-medium ${getFreshnessColor(stats.latestPriceUpdate)}`}>
                {formatAge(stats.latestPriceUpdate)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Tickers with prices:</span>
              <span className="font-medium text-gray-900">{stats.priceCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Tickers with earnings:</span>
              <span className="font-medium text-gray-900">{stats.earningsCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Tickers with SMAs:</span>
              <span className="font-medium text-gray-900">{stats.smaCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Last SMA refresh:</span>
              <span className={`font-medium ${getFreshnessColor(stats.latestSmaUpdate)}`}>
                {formatAge(stats.latestSmaUpdate)}
              </span>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100">
            <Link to="/" className="text-xs text-blue-600 hover:text-blue-800 font-medium">
              → Go to Watchlist to refresh prices
            </Link>
          </div>
        </div>

        {/* Option Chain Cache */}
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">📈 Option Chain Cache</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Cached tickers (fresh):</span>
              <span className="font-medium text-green-600">{stats.optionCacheFreshCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total cached:</span>
              <span className="font-medium text-gray-900">{stats.optionCacheCount}</span>
            </div>
          </div>
          {stats.optionCacheEntries.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="text-[11px] text-gray-500 mb-1">Recent:</div>
              <div className="flex flex-wrap gap-1">
                {stats.optionCacheEntries.map(e => (
                  <span
                    key={e.ticker}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      (Date.now() - new Date(e.fetched_at).getTime()) < SIX_HOURS_MS
                        ? 'bg-green-50 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {e.ticker} ({formatAge(e.fetched_at)})
                  </span>
                ))}
                {stats.optionCacheCount > 10 && (
                  <span className="text-[10px] text-gray-400">+{stats.optionCacheCount - 10} more</span>
                )}
              </div>
            </div>
          )}
          {stats.optionCacheEntries.length === 0 && (
            <p className="mt-3 text-xs text-gray-400 italic">
              No option chains cached yet. Ask about options for a ticker in chat to populate.
            </p>
          )}
        </div>

        {/* Technical Indicators */}
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">📐 Technical Indicators</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Tickers cached:</span>
              <span className="font-medium text-gray-900">{stats.indicatorCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Last update:</span>
              <span className={`font-medium ${getFreshnessColor(stats.latestIndicatorUpdate)}`}>
                {formatAge(stats.latestIndicatorUpdate)}
              </span>
            </div>
          </div>
          {stats.indicatorEntries.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="text-[11px] text-gray-500 mb-1">Recent:</div>
              <div className="flex flex-wrap gap-1">
                {stats.indicatorEntries.map(e => (
                  <span
                    key={e.ticker}
                    className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-gray-700"
                  >
                    {e.ticker} ({formatAge(e.updated_at)})
                  </span>
                ))}
              </div>
            </div>
          )}
          {stats.indicatorEntries.length === 0 && (
            <p className="mt-3 text-xs text-gray-400 italic">
              No indicators cached yet. SMAs are computed and stored when "Refresh SMAs" is clicked on the Watchlist page.
            </p>
          )}
        </div>

        {/* Data Flow Info */}
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">ℹ️ How Data Flows</h2>
          <ul className="text-xs text-gray-600 space-y-1.5">
            <li><strong>Prices:</strong> Manual "Refresh Prices" on Watchlist → writes to <code>watchlist.price_current</code> + <code>price_updated_at</code></li>
            <li><strong>Earnings:</strong> Separate "Fetch Earnings" → writes to <code>earnings_calendar</code> table</li>
            <li><strong>SMAs:</strong> Manual "Refresh SMAs" on Watchlist → Twelve Data API → writes to <code>watchlist.sma_20/50/200</code> + <code>sma_updated_at</code></li>
            <li><strong>Options:</strong> Fetched by chat on-demand → writes to <code>option_chain_cache</code> table</li>
            <li><strong>Indicators:</strong> Computed from historical bars → writes to <code>technical_indicators</code> table</li>
            <li><strong>Chat:</strong> Reads all cached data from Supabase. Shows stale warning if prices &gt; 6h old.</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
